/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Box from '@material-ui/core/Box';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import Accordion from '@ion/dls/src/components/accordion/Accordion';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {ButtonSpinner} from '@ion/dls/src/components/loading/ButtonSpinner';
import {useAnimatedMount} from '@ion/dls/src/hooks/useAnimatedMount';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {AvatarIconSize, DLSTheme} from '@ion/dls/src/types/types';
import {BodyTypography} from '@ion/dls/src/typography/BodyTypography';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';
import {editAsset, showAssetDialog} from '@/store/actions/dashboard';
import {
  editAssetErrorSelector,
  editAssetLoadingSelector,
  selectedAssetSelector
} from '@/store/selectors/dashboard-selectors';
import Images from '../img/ImagePaths';
import BaseDialog from './BaseDialog';

export const styles = (theme: DLSTheme) => ({
  content : {
    display                        : 'flex',
    flexDirection                  : 'column',
    padding                        : '48px 100px 0px 100px',
    width                          : 740,
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  contentHeight : {
    minHeight : 500
  },
  ctaLinkStyle : {
    paddingBottom : '15px',
    width         : 200,
    textAlign     : 'left'
  },
  sideView : {
    color      : dlsColor('grey2'),
    height     : '64px',
    fontWeight : 500,
    cursor     : 'pointer'
  },
  activeTab : {
    borderBottom : '1px solid #0097AF'
  },
  img : {
    borderRadius : '50%',
    height       : '100px',
    width        : '100px',
    marginRight  : 15
  },
  header : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'flex-start',
    wordBreak      : 'break-all'
  },
  detailValues : {
    color    : dlsColor('grey3'),
    fontSize : '14px'
  },
  actions : {
    display        : 'flex',
    paddingTop     : 30,
    justifyContent : 'flex-start'
  },
  assetField : {
    width : 330
  },
  margin : {
    marginLeft : '205px'
  },
  button : {
    color : dlsColor('white'),
    width : '170px'
  },
  color : {
    color : dlsColor('caBlue')
  },
  details : {
    color : dlsColor('black'),
    width : 200
  },
  field : {
    paddingTop : '16px'
  },
  name : {
    padding    : 0,
    fontWeight : 500,
    fontSize   : '40px',
    color      : '#4F4F4F',
    wordBreak  : 'break-all'
  }
});

/**
 * This component provides a dialog to manage the current user's Asset
 * information.
 *
 * ```typescript
 *
 * import {AssetDialog} from '@ion-bmw-portal/src/components/dialog';
 *
 * <AssetDialog />
 *
 * ```
 *
 */
export const AssetDialog = () => {
  const dispatch                      = useDispatch();
  const {isVisible, onRemove}         = useAnimatedMount();
  const useStyles                     = useExtendStyles(styles);
  const classes                       = useStyles();
  const selectedAsset                 = useSelector(selectedAssetSelector);
  const loading                       = useSelector(editAssetLoadingSelector);
  const error                         = useSelector(editAssetErrorSelector);
  const [assetName, setName]          = useState(selectedAsset.name);
  const [subheader, setSubheader]     = useState(selectedAsset.name);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [expanded, setExpanded]       = useState(false);

  const assetLabels = {...translate('common:dialogs.asset.fields', {returnObjects: true})};
  const title       = translate('common:dialogs.asset.title');

  useEffect(() => {
    setSubheader(selectedAsset.name);
  }, [selectedAsset]);

  useEffect(() => {
    if (!loading && !error) {
      onToggleExpand(false);
    }
  }, [loading, error]);

  function onClose() {
    onRemove(function() {
      dispatch(showAssetDialog(false));
    });
  };

  function handleAssetName({target:{value, name}}) {
    const enable = (value.length > 0 || value !== selectedAsset[name]);
    setSaveEnabled(enable);
    setName(value);
  }

  function saveAssetName() {
    const asset = {
      id   : selectedAsset.id,
      name : assetName
    };
    dispatch(editAsset(asset));
  }

  function onToggleExpand(isExpanded) {
    setExpanded(isExpanded);
    setName(selectedAsset.name);
    const value = isExpanded ? translate('common:dialogs.asset.expanded.assetHeader') : selectedAsset.name;
    setSubheader(value);
  }

  return (
    <BaseDialog show={isVisible} title={title} onClose={onClose}>
      <div className={classnames(classes.content, classes.contentHeight)}>
        <Box>
          <Box display='flex' alignItems='center'>
            <Avatar src={Images.BMWAvatar}
              className={classes.img}
              size={AvatarIconSize.large} />
            <Box>
              <BodyTypography className={classes.name}
                component='span'>{selectedAsset.name}
              </BodyTypography>
              <LabelTypography>{translate('common:dialogs.asset.sideHeader')}</LabelTypography>
            </Box>
          </Box>
        </Box>
        <div>
          {Object.keys(assetLabels).map((field, i) => (
            <div key={`field-${field}`} className={classes.field}>
              <Accordion id={`field-${field}`}
                children=''
                borderBottom={'none'}
                disableExpansion={i !== 0}
                expanded={(i === 0 && expanded) || false}
                onExpand={function(e) {
                  onToggleExpand(e);
                }}
                header={
                  <div className={classes.header}>
                    <div className={classes.details}>
                      {translate(`common:dialogs.asset.fields.${field}`)}
                    </div>
                    <div className={classes.detailValues}>{i !== 0 ? selectedAsset[field] : subheader}</div>
                  </div>
                }
                details={
                  <><div className={classes.margin}>
                    <div>
                      <FormTextField className={classes.assetField}
                        name={field}
                        maxLength={32}
                        placeholder={translate(`common:dialogs.asset.fields.${field}`)}
                        value={assetName}
                        defaultValue={selectedAsset[field]}
                        onChange={handleAssetName} />
                    </div>
                    <div className={classes.actions} >
                      <PrimaryButton className={classes.button}
                        disabled={!saveEnabled}
                        onClick={saveAssetName}>
                        {loading ? <ButtonSpinner /> : translate('common:dialogs.shared.saveChanges')}
                      </PrimaryButton>
                      <TertiaryButton className={classes.color}
                        onClick={function() {
                          onToggleExpand(false);
                        }} >{translate('common:cancel')}
                      </TertiaryButton>
                    </div>
                  </div>
                  </>}/>
            </div>
          ))
          }
        </div>
      </div>
    </BaseDialog>
  );
};

export default AssetDialog;
